"use client";

import React, { createContext, useContext, useState, useCallback } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useLocale } from "next-intl";
import { useCurrentUser } from "@/modules/auth/context/CurrentUserContext";
import { isAccountActive, isTeacherAccount, resolvePrimaryAccount } from "@/modules/auth/utils/userAccess";
import InactiveAccountModal from "@/components/auth/InactiveAccountModal";
import { getRegionLocaleHref } from "@/services/region";
import {
  FREE_TRIAL_PATH,
  isFreeTrialPath,
  needsProfileCompletion,
} from "@/modules/auth/utils/profileCompletion";

export type EmailVerificationContextValue = {
  isModalOpen: boolean;
  /** True whenever the resolved primary account (never the sub-user's own flag) is inactive. */
  isUserInactive: boolean;
  openVerificationModal: (pendingDestination?: string) => void;
  closeVerificationModal: () => void;
  checkVerificationAndProtect: (destinationUrl?: string) => boolean;
};

const EmailVerificationContext = createContext<EmailVerificationContextValue | null>(null);

export function EmailVerificationProvider({ children }: { children: React.ReactNode }) {
  const { user } = useCurrentUser();
  const router = useRouter();
  const locale = useLocale();
  const pathname = usePathname();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingDestination, setPendingDestination] = useState<string | null>(null);

  // Teacher accounts are exempt: `is_active` means something different for
  // them. A teacher never self-registers - an admin creates the account (see
  // TeacherRepository::create, which writes `is_active` from the admin form and
  // never sets `email_verified_at`), so an inactive teacher is one an admin
  // hasn't activated or has deliberately switched off. Neither is "you have an
  // unread verification email", and prompting for a code they were never sent
  // left teachers stuck behind a modal they had no way to satisfy. Teachers log
  // in and use the dashboard normally; the backend's EnsureUserIsActive
  // middleware still rejects their state-changing requests while inactive.
  const isUserInactive = Boolean(user) && !isTeacherAccount(user) && !isAccountActive(user);

  const justRegistered = typeof window !== "undefined" && window.sessionStorage.getItem("justRegistered") === "true";

  const openVerificationModal = useCallback((destination?: string) => {
    if (destination) {
      setPendingDestination(destination);
    }
    setIsModalOpen(true);
  }, []);

  // Keeps the modal in sync with the resolved primary account's activation
  // state — not just opening it when a session becomes inactive, but also
  // closing it again once it becomes active (e.g. the primary account
  // finishes verifying, or a session switches into an active sub-user).
  // Without the close side, a modal opened once (even transiently) stayed
  // stuck open for the rest of the app's lifetime, since this provider is
  // mounted once at the root and never remounts across navigations.
  React.useEffect(() => {
    setIsModalOpen(isUserInactive);
  }, [isUserInactive]);

  const closeVerificationModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  // Returns true if verification is needed (and opens modal), false if user is active / allowed
  const checkVerificationAndProtect = useCallback(
    (destinationUrl?: string) => {
      if (isUserInactive) {
        openVerificationModal(destinationUrl || pathname);
        return true; // Protected
      }
      return false; // Allowed
    },
    [isUserInactive, openVerificationModal, pathname]
  );

  const handleVerificationSuccess = useCallback(() => {
    setIsModalOpen(false);
    if (typeof window !== "undefined") {
      window.sessionStorage.removeItem("justRegistered");
    }
    const dest = pendingDestination;
    setPendingDestination(null);

    // A fresh sign-up verifying its email still has its profile to fill in,
    // so it goes on to the free-trial wizard that asks for it - wherever the
    // code was entered, and ahead of any other destination. Verifying does not
    // change profile_completed, so the pre-refetch `user` answers this. Already
    // on the wizard, there is nowhere to go: EmailVerificationGuard renders it
    // in place.
    if (needsProfileCompletion(user)) {
      if (!isFreeTrialPath(pathname) && !isFreeTrialPath(dest)) {
        router.push(getRegionLocaleHref(pathname, locale, FREE_TRIAL_PATH));
        return;
      }
    }

    if (dest) {
      router.push(dest);
    }
  }, [pendingDestination, router, user, pathname, locale]);

  // The verification email always belongs to the primary account — a
  // switched-into sub-user session has no independent activation state (and
  // may have no email of its own), so the modal must never show or send
  // codes against the sub-user's own record.
  const primaryAccountEmail = resolvePrimaryAccount(user)?.email as string | undefined;

  return (
    <EmailVerificationContext.Provider
      value={{
        isModalOpen,
        isUserInactive,
        openVerificationModal,
        closeVerificationModal,
        checkVerificationAndProtect,
      }}
    >
      {children}
      <InactiveAccountModal
        isOpen={isModalOpen}
        userEmail={primaryAccountEmail}
        initialCodeSent={justRegistered}
        onClose={closeVerificationModal}
        onSuccess={handleVerificationSuccess}
      />
    </EmailVerificationContext.Provider>
  );
}

export function useEmailVerificationGuard() {
  const ctx = useContext(EmailVerificationContext);
  if (!ctx) {
    throw new Error("useEmailVerificationGuard must be used within an EmailVerificationProvider");
  }
  return ctx;
}
